// client/src/coms/views/games_parts/useGameSession.js
// Mirrors one hosted game session from its live stream and sends player actions. The server decides
// every rule; this hook only displays what it is told.
import { useCallback, useEffect, useRef, useState } from 'react';

const ERRORS = {
  host_only: 'Only the host can do that.',
  not_enough_players: 'More players are needed to start.',
  too_many_players: 'There are too many players for this game.',
  players_disconnected: 'Wait until everyone is connected.',
  session_full: 'This game is full.',
  game_in_progress: 'This game has already started.',
  no_game_running: 'No game is running.',
  game_not_finished: 'The game is not over yet.',
  not_seated: 'Join the game first.',
  removed_by_host: 'The host removed you from this game.',
  session_not_found: 'This game is no longer available.',
  stale_game: 'That move was for an earlier game.',
  invalid_move: 'That move is not allowed.',
  move_too_large: 'That move is not allowed.',
  too_many_sessions: 'You are already hosting the maximum number of games.',
  not_a_server_member: 'You are not a member of that server.',
  invite_friends_only: 'You can only invite friends.',
  slow_down: 'Slow down a little.',
  g_not_your_turn: "It isn't your turn.",
  g_game_over: 'The game is over.',
  peer_offline: 'That player is not connected.',
  pk_bad_amount: 'That bet size is not allowed.',
  pk_cannot_check: 'You cannot check: there is a bet to call.',
  pk_no_hand: 'Wait for the next hand.',
  watch_channel_forbidden: 'You do not have access to the voice channel this watch session is in.',
};

const SIGNAL_REPLAY_MS = 8000;

export function describeGameError(code) {
  if (ERRORS[code]) return ERRORS[code];
  // GameNest rule errors (for example "ttt_position_occupied") read reasonably as words.
  if (typeof code === 'string' && /^[a-z0-9_]+$/.test(code)) {
    const words = code.replace(/^[a-z0-9]+_/, '').replace(/_/g, ' ');
    return `Not allowed: ${words}.`;
  }
  return 'Something went wrong. Try again.';
}

export function isNewerSessionView(current, next) {
  if (!next || typeof next !== 'object') return false;
  if (!current || current.id !== next.id) return true;
  return Number(next.rev) >= Number(current.rev);
}

export async function gamesApi(path, { method = 'GET', body } = {}) {
  const response = await fetch(path, {
    method,
    credentials: 'include',
    cache: 'no-store',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, data };
}

export default function useGameSession(sessionId) {
  const [session, setSession] = useState(null);
  // Tracked per session id, so a game that closed earlier cannot close the next one.
  const [closedId, setClosedId] = useState('');
  const closed = Boolean(sessionId) && closedId === sessionId;
  const [error, setError] = useState('');
  const [pending, setPending] = useState('');
  const sessionRef = useRef(null);
  // WebRTC signalling for client-hosted arcade games arrives on the same stream.
  const signalListeners = useRef(new Set());
  // Signals from the last few seconds, replayed to a listener that subscribes late (the match screen
  // mounting just after a player knocked), so the first knock is not lost.
  const recentSignals = useRef([]);

  const apply = useCallback((next) => {
    if (!isNewerSessionView(sessionRef.current, next)) return;
    sessionRef.current = next;
    setSession(next);
  }, []);

  useEffect(() => {
    sessionRef.current = null;
    recentSignals.current = [];
    setSession(null);
    setError('');
    if (!sessionId || typeof window === 'undefined') return undefined;
    let cancelled = false;
    gamesApi(`/api/games/sessions/${encodeURIComponent(sessionId)}`).then(({ ok, data }) => {
      if (cancelled) return;
      if (ok) apply(data.session);
      else setClosedId(sessionId);
    });
    if (typeof window.EventSource === 'undefined') return () => {
      cancelled = true;
    };
    const source = new window.EventSource(`/api/games/sessions/${encodeURIComponent(sessionId)}/events`, { withCredentials: true });
    source.onmessage = (message) => {
      try {
        const event = JSON.parse(String(message.data || '{}'));
        if (event.type === 'game_session') apply(event.session);
        if (event.type === 'game_signal') {
          const now = Date.now();
          recentSignals.current = recentSignals.current.filter((item) => now - item.at < SIGNAL_REPLAY_MS).slice(-40);
          recentSignals.current.push({ at: now, event });
          signalListeners.current.forEach((listener) => listener(event));
        }
        if (event.type === 'game_session_closed') {
          setClosedId(sessionId);
          source.close();
        }
      } catch {}
    };
    return () => {
      cancelled = true;
      source.close();
    };
  }, [apply, sessionId]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = window.setTimeout(() => setError(''), 3500);
    return () => window.clearTimeout(timer);
  }, [error]);

  const act = useCallback(async (action, payload = {}) => {
    if (!sessionId) return null;
    setPending(action);
    try {
      const result = await gamesApi(`/api/games/sessions/${encodeURIComponent(sessionId)}/actions`, { method: 'POST', body: { ...payload, action } });
      if (result.data && result.data.session) apply(result.data.session);
      if (result.data && result.data.closed) setClosedId(sessionId);
      if (!result.ok) {
        if (result.status === 404) setClosedId(sessionId);
        setError(describeGameError(result.data && result.data.error));
      }
      return result;
    } catch {
      setError('Could not reach the game server.');
      return null;
    } finally {
      setPending('');
    }
  }, [apply, sessionId]);

  const onSignal = useCallback((listener) => {
    signalListeners.current.add(listener);
    const now = Date.now();
    recentSignals.current.filter((item) => now - item.at < SIGNAL_REPLAY_MS).forEach((item) => listener(item.event));
    return () => signalListeners.current.delete(listener);
  }, []);

  // Resolves to { ok, status, error } so the transport can act on why a signal was refused.
  const sendSignal = useCallback(async (to, data) => {
    if (!sessionId) return { ok: false, status: 0, error: 'no_session' };
    const result = await gamesApi(`/api/games/sessions/${encodeURIComponent(sessionId)}/signal`, { method: 'POST', body: { to, data } }).catch(() => null);
    if (!result) return { ok: false, status: 0, error: 'network' };
    return { ok: result.ok, status: result.status, error: String((result.data && result.data.error) || '') };
  }, [sessionId]);

  // A token for the arcade relay room (used only when a direct WebRTC link fails).
  const getRelayToken = useCallback(async () => {
    if (!sessionId) return null;
    const result = await gamesApi(`/api/games/sessions/${encodeURIComponent(sessionId)}/relay`, { method: 'POST', body: {} }).catch(() => null);
    if (!result) return null;
    return result.ok ? result.data : { error: String((result.data && result.data.error) || 'relay_unavailable') };
  }, [sessionId]);

  return { session, closed, error, pending, act, setError, onSignal, sendSignal, getRelayToken };
}
